import { CSSProperties, ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, Info, X } from 'lucide-react';
import { useTranslation } from '../lib/i18n';
import { handleModalEscape, pushModalEscape } from '../lib/modal-escape';
import { useNarrow } from '../lib/use-narrow';
import { RailCatcher } from './RailCatcher';
import { IconButton, ScrollArea } from './symphony/ui';
import paneScrim from './pane-scrim.module.css';
import styles from './Modal.module.css';

/** Keeps the rail inert without letting a press there dismiss, when dismissal is off. */
const noop = () => {};

// The DS modal widths (Figma "Modal / Drawer" → Custom modal layout, node 1136:1700).
const SIZE_WIDTH: Record<ModalSize, number> = {
  minimum: 512,
  small: 672,
  medium: 768,
  maximum: 1280,
};

export type ModalSize = 'minimum' | 'small' | 'medium' | 'maximum';

// The one modal shell (the Figma "Modal / Drawer" DS component, node 1132:1576): a scrim over a
// centered card with a top-inline-end close (✕), an optional header (title + help icon + description),
// a content slot, an optional right-aligned footer for the action buttons, and an optional grey
// footnote strip. Token-driven, so it takes on whichever brand's palette is active. Portaled to
// <body> to escape any transformed or overflow-clipped ancestor. Closes on Escape and on a scrim
// click; the footer buttons are supplied by the caller so each modal keeps its own actions.
export function Modal({
  open,
  onClose,
  title,
  titleInfo,
  description,
  children,
  footer,
  footnote,
  headerSeparator = false,
  footerSeparator = false,
  size,
  width = 440,
  maxHeightPx,
  labelledBy,
  cardClassName,
  contentClassName,
  fullScreenOnSmall = true,
  scrollBody = false,
  dismissOnScrimClick = true,
  hideClose = false,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  // The DS header's optional help/info circle. Pass the tooltip text; renders an 18px info glyph.
  titleInfo?: string;
  // The DS header's secondary line under the title (14/18, secondary ink).
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  // The DS grey small-print strip at the very bottom of the card (legal notes, hints, help links).
  footnote?: ReactNode;
  // The DS hairline between header and content. Off by default so the existing dialogs keep their
  // plain look; the feature modals built to the DS opt in.
  headerSeparator?: boolean;
  // The DS hairline between content and footer.
  footerSeparator?: boolean;
  // The DS width tokens (Minimum 512 / Small 672 / Medium 768 / Maximum 1280). Overrides `width`.
  size?: ModalSize;
  width?: number;
  // Cap the card at this pixel height (never past the viewport), so the body scrolls inside a fixed-height
  // modal rather than the card growing with its content. Pair with `scrollBody`. Overrides the plain
  // `.cardScroll` viewport cap, and yields to the full-screen-on-small variant (it is a class, not an
  // inline style — see `.cardCap`). **Design QA asks EVERY centered modal to sit at 650** (Monday
  // 12771405229) — pass `maxHeightPx={650} scrollBody`; test/modal-max-height.test.ts holds the roster
  // and the reason for each modal that is deliberately not on it.
  maxHeightPx?: number;
  labelledBy?: string;
  // Below 700px, fill the viewport instead of floating a card in it: edge-to-edge scrim, no radius,
  // no shadow, and the card scrolls. This is the DEFAULT on mobile — a floating card in a phone
  // viewport reads as an unfinished sheet, and native presents these same dialogs full-screen, so
  // this is the parity behaviour. A dialog that genuinely wants to stay a centered card on a phone
  // opts out with `fullScreenOnSmall={false}`.
  fullScreenOnSmall?: boolean;
  // Cap the card at the viewport height and scroll the BODY (not the whole card) inside a ScrollArea,
  // so the header + footer stay pinned while long content scrolls. Opt-in — a modal whose content owns
  // an overflowing child (an absolutely-positioned dropdown, e.g. the phone country picker) must NOT
  // set it, since a scroll container would clip that child.
  scrollBody?: boolean;
  // Whether a press outside the card dismisses it. On by default, because for most dialogs clicking
  // away IS the cheap way out. A flow that would lose real progress turns it off — a code already
  // sent to a phone cannot be un-sent, and a stray click beside the card should not cost it. Escape
  // still closes either way: it is a deliberate keypress, and the expected dismissal for a dialog.
  dismissOnScrimClick?: boolean;
  // Suppress the shell's own way out — both the desktop ✕ AND the full-screen "‹ back". For a
  // full-screen sheet whose OWN chrome already draws a back control (SkillsModalMobile) — a second one
  // would just be doubled. Escape and the scrim still close, so the modal is never trapped.
  hideClose?: boolean;
  // Optional class on the dialog card. Lets a caller re-assert its own palette tokens
  // (--surface-base-rgb / --ink-rgb / --sym-*) — the shell portals to <body>, so a modal that
  // must NOT inherit the active brand's body palette (e.g. the always-light package picker)
  // scopes the right tokens here rather than the shell hardcoding a look.
  cardClassName?: string;
  // Optional class on the content slot, ADDED to `.content`/`.contentBare` rather than replacing
  // them. `.content` is a plain block box — a caller whose child relies on flex-shrink (e.g. a
  // fixed-height reading pane that must reclaim space on a short viewport) needs this to turn the
  // slot itself into a flex column with `min-height: 0`, or the child's own flex properties are
  // inert (its parent isn't a flex container) and a short viewport clips the footer instead of
  // shrinking the content. Scoped per-caller rather than added to the shared class because most of
  // the ~20 existing dialogs pass plain block children that rely on normal margin flow.
  contentClassName?: string;
}) {
  const { t } = useTranslation();
  // Match the 700px breakpoint the full-screen CSS below triggers at (NOT useNarrow's 720 default), so the
  // back button appears exactly when the card fills the viewport.
  const fullScreen = useNarrow(701);
  // Read through a ref so a fresh inline `onClose` doesn't re-register (and reorder) the escape stack.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) {
      return;
    }
    // Escape closes THIS modal and nothing else — see lib/modal-escape.ts for why it must be captured
    // and swallowed rather than shared with the panels listening underneath.
    //
    // Known latent limitation, no call site hits it today: the stack's "innermost" is really "registered
    // last", and React runs effects CHILD-BEFORE-PARENT. So two Modals mounting in the SAME commit (a
    // modal that renders another already-open modal in its own subtree) would push the CHILD first and the
    // OUTER one last, leaving the outer on top of the stack — one Escape would close the outer and strand
    // the inner. Every current nesting opens the second modal in a LATER commit (a click), where the order
    // is correct. If a same-commit pair ever appears, the fix is an explicit depth/ordinal on the stack
    // entry rather than relying on registration order.
    const pop = pushModalEscape(() => onCloseRef.current());
    const onKey = (e: KeyboardEvent) => handleModalEscape(e);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      pop();
    };
  }, [open]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  const hasHeader = Boolean(title || description || titleInfo);
  const modalWidth = size ? SIZE_WIDTH[size] : width;

  const contentClasses = [
    hasHeader ? styles.content : styles.contentBare,
    hasHeader && headerSeparator ? styles.contentUnderSeparator : undefined,
    contentClassName,
  ].filter(Boolean) as string[];

  // The DS draws its header/footer rules inside a 20-of-clearance block on BOTH sides (Figma
  // 12889-206300: the header container is py-20 with the rule under it, the footer's button row py-20
  // under its own rule). The shell's plain 16 left the two hairlines 4 short, so the rule is nudged to
  // 20 only for the dialogs that draw it — a rule-less header/footer keeps its 16.
  const headerClasses = [styles.header, headerSeparator ? styles.headerAboveSeparator : undefined]
    .filter(Boolean)
    .join(' ');
  const footerClasses = [styles.footer, footerSeparator ? styles.footerUnderSeparator : undefined]
    .filter(Boolean)
    .join(' ');

  // Full-screen on mobile presents like a pushed native screen, so the way out is a leading "‹ back"
  // (top-inline-start) rather than the floating ✕ — the same chrome the native full-screen sheets and
  // TaskDetailMobile/SkillsModalMobile carry. `hideClose` still wins: a modal that draws its own back
  // control (SkillsModalMobile) opts out of both so it isn't doubled.
  const fullScreenBack = fullScreenOnSmall && fullScreen && !hideClose;
  const showClose = !hideClose && !fullScreenBack;

  // The scrim is scoped to the CONTENT PANE so the sidebar/nav stays crisp (per the Figma — only the
  // content behind the card blurs, not the rail); `paneScrim` keeps that edge tracking the rail as it
  // collapses/expands, and the RailCatcher below makes the rail inert while the dialog is open.
  return createPortal(
    <div
      className={[paneScrim.paneScrim, styles.overlay, fullScreenOnSmall ? styles.fullScreenScrim : undefined]
        .filter(Boolean)
        .join(' ')}
      // Scrim click closes — but only a press that both starts AND ends on the scrim, so a text selection
      // that drifts out of the card doesn't dismiss it.
      onMouseDown={(e) => {
        if (dismissOnScrimClick && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <RailCatcher onClose={dismissOnScrimClick ? onClose : noop} zIndex={1000} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={[
          styles.card,
          cardClassName,
          fullScreenOnSmall ? styles.fullScreenCard : undefined,
          scrollBody ? styles.cardScroll : undefined,
          maxHeightPx ? styles.cardCap : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
        style={
          {
            '--modal-w': `${modalWidth}px`,
            ...(maxHeightPx ? { '--modal-max-h': `${maxHeightPx}px` } : {}),
          } as CSSProperties
        }
      >
        {fullScreenBack && (
          <IconButton aria-label={t('common.back')} variant="text" size="medium" onClick={onClose} className={styles.back}>
            <ChevronLeft size={24} aria-hidden className="dir-flip" />
          </IconButton>
        )}
        {showClose && (
          <button type="button" onClick={onClose} aria-label={t('common.close')} className={`orion-modal-close ${styles.close}`}>
            <X size={16} aria-hidden />
          </button>
        )}
        {hasHeader && (
          <div className={headerClasses}>
            <div className={styles.headerContent}>
              {(title || titleInfo) && (
                <div className={styles.titleRow} id={labelledBy}>
                  {title && <span className={styles.title}>{title}</span>}
                  {titleInfo && <Info className={styles.titleInfo} size={18} aria-label={titleInfo} />}
                </div>
              )}
              {description && <div className={styles.description}>{description}</div>}
            </div>
          </div>
        )}
        {hasHeader && headerSeparator && <div className={styles.separator} />}
        {scrollBody ? (
          <ScrollArea className={[...contentClasses, styles.scrollBody].join(' ')}>{children}</ScrollArea>
        ) : (
          <div className={contentClasses.join(' ')}>{children}</div>
        )}
        {footer && (
          <>
            {footerSeparator && <div className={styles.separator} />}
            <div className={footerClasses}>{footer}</div>
          </>
        )}
        {footnote && <div className={styles.footnote}>{footnote}</div>}
      </div>
    </div>,
    document.body,
  );
}
